export const API_URL = process.env.REACT_APP_API_URL
  || (process.env.NODE_ENV === 'production'
    ? 'https://api.chooziq.com/api'
    : 'http://localhost:8000/api');

const getHeaders = (useJson = true, extraHeaders = {}) => {
  const token = sessionStorage.getItem('chooziq_token') || localStorage.getItem('chooziq_token');
  const headers = {
    Accept: 'application/json',
    ...extraHeaders,
  };

  if (useJson && !Object.prototype.hasOwnProperty.call(headers, 'Content-Type')) {
    headers['Content-Type'] = 'application/json';
  }

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return headers;
};

const handleResponse = async (response) => {
  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch (e) {
      errorData = { message: 'An unexpected error occurred. Please try again.' };
    }

    if (response.status === 401) {
      sessionStorage.removeItem('chooziq_token');
      localStorage.removeItem('chooziq_token');
      localStorage.removeItem('chooziq_user');
    }

    // Standardize error object
    const error = new Error(errorData.message || 'Something went wrong');
    error.status = response.status;
    error.data = errorData;
    error.errors = errorData.errors || null; // For validation errors

    throw error;
  }
  if (response.status === 204) {
    return null;
  }
  return response.json();
};

const normalizePaginatedResponse = (json) => {
  if (
    json &&
    typeof json === 'object' &&
    Array.isArray(json.data) &&
    (json.meta || json.current_page)
  ) {
    const data = json.data;
    Object.defineProperty(data, 'meta', {
      value: json.meta || {
        current_page: json.current_page,
        per_page: json.per_page ?? json.perPage ?? data.length,
        total: json.total ?? data.length,
        last_page: json.last_page ?? json.lastPage ?? 1,
      },
      enumerable: false,
      configurable: true,
      writable: false,
    });
    return data;
  }
  return json;
};

const isPaginatedResponse = (json) => {
  return (
    json &&
    typeof json === 'object' &&
    Array.isArray(json.data) &&
    (json.meta || json.current_page)
  );
};

const request = async (endpoint, options = {}) => {
  const response = await fetch(endpoint, options);
  const json = await handleResponse(response);
  return json;
};

export const api = {
  get: async (endpoint, options = {}) => {
    const response = await request(`${API_URL}${endpoint}`, {
      headers: getHeaders(true, options.headers || {}),
      credentials: options.credentials || 'include',
      method: 'GET',
    });
    return normalizePaginatedResponse(response);
  },

  paginate: async (endpoint, options = {}) => {
    const response = await request(`${API_URL}${endpoint}`, {
      headers: getHeaders(true, options.headers || {}),
      credentials: options.credentials || 'include',
      method: 'GET',
    });
    if (isPaginatedResponse(response)) {
      return response;
    }
    return {
      data: Array.isArray(response) ? response : [response],
      meta: {
        current_page: 1,
        per_page: Array.isArray(response) ? response.length : 1,
        total: Array.isArray(response) ? response.length : 1,
        last_page: 1,
      },
    };
  },

  post: async (endpoint, data, options = {}) => {
    const isFormData = data instanceof FormData;
    const response = await request(`${API_URL}${endpoint}`, {
      method: 'POST',
      headers: getHeaders(!isFormData, options.headers || {}),
      credentials: options.credentials || 'include',
      body: isFormData ? data : JSON.stringify(data),
    });
    return response;
  },

  put: async (endpoint, data, options = {}) => {
    const isFormData = data instanceof FormData;
    const response = await request(`${API_URL}${endpoint}`, {
      method: 'PUT',
      headers: getHeaders(!isFormData, options.headers || {}),
      credentials: options.credentials || 'include',
      body: isFormData ? data : JSON.stringify(data),
    });
    return response;
  },

  delete: async (endpoint, options = {}) => {
    const response = await request(`${API_URL}${endpoint}`, {
      method: 'DELETE',
      headers: getHeaders(true, options.headers || {}),
      credentials: options.credentials || 'include',
    });
    return response;
  },
};
